<script lang="ts">
export default {
  name: 'TableSkeleton',
};
</script>

<script setup lang="ts">
import Skeleton from '@/components/v2/Skeleton.vue';

const props = withDefaults(
  defineProps<{
    columns: string[];
    rows?: number;
    linesPerRow?: number;
    showFilterBar?: boolean;
    showTitle?: boolean;
  }>(),
  {
    rows: 8,
    linesPerRow: 1,
    showFilterBar: true,
    showTitle: true,
  },
);

const FILTER_PILL_WIDTHS = ['6rem', '5rem', '7rem', '5.5rem'];
const CELL_WIDTHS = ['80%', '55%', '70%', '45%', '85%', '60%', '75%', '50%'];

function cellWidth(row: number, column: number, line: number): string {
  const width = CELL_WIDTHS[(row * props.columns.length + column) % CELL_WIDTHS.length];
  return line === 1 ? width : `calc(${width} * 0.6)`;
}
</script>

<template>
  <div
    class="w-full"
    data-testid="table-skeleton">
    <div
      v-if="showFilterBar"
      class="flex items-center gap-2 px-4 py-2">
      <Skeleton
        v-for="(width, index) in FILTER_PILL_WIDTHS"
        :key="index"
        shape="rectangle"
        :width="width"
        height="2rem"
        class="rounded-full" />
    </div>

    <div class="px-4 py-4">
      <slot name="above-table" />

      <div
        v-if="showTitle"
        class="mb-3 flex items-center gap-3">
        <Skeleton
          width="14rem"
          height="1.5rem" />
        <Skeleton
          width="6rem"
          height="1rem" />
      </div>

      <table class="apptable dense">
        <thead>
          <tr>
            <td
              v-for="label in columns"
              :key="label"
              class="nosort">
              {{ label }}
            </td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row">
            <td
              v-for="(label, column) in columns"
              :key="label">
              <div class="skeleton-cell">
                <Skeleton
                  v-for="line in linesPerRow"
                  :key="line"
                  :width="cellWidth(row, column, line)"
                  height="0.75rem" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.skeleton-cell {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  height: 42px;
}
</style>
